import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { FilterIcon, SearchIcon, XIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Separator } from '@/components/ui/separator';
import { useEventCallback } from '../../ui/hooks';
import { isNull } from '../../ui/utils';
import { FilterTypeComponents } from './FilterControls';
import { useFilterControls, useSearchFieldOptions, type SearchFieldOption } from './FilterControls/useFilterControls';

/**
 * The list toolbar's search box and its one panel. Focusing the empty box, or pressing Filters,
 * opens the panel on the filters (and the active-search chips). Typing switches it to the fields
 * the term can search; choosing one applies the search and clears the box.
 */
export function SearchBoxFilterButton({ searchPlaceholder }: { searchPlaceholder?: string }) {
  const [filterControls, activeFiltersCount] = useFilterControls({ components: FilterTypeComponents });
  const [searchFields, activeSearches] = useSearchFieldOptions();
  const numSet = activeFiltersCount + activeSearches.length;

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const [input, setInput] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSearchFields, setShowSearchFields] = useState(false);
  const hasInput = input.trim().length ? true : false;

  // While the box has focus, the panel follows what is typed.
  const [inputFocused, setInputFocused] = useState(false);
  useEffect(() => {
    if (inputFocused) setShowSearchFields(hasInput);
  }, [inputFocused, hasInput]);

  const handleInputFocus = useEventCallback(() => {
    if (!menuOpen) {
      setShowSearchFields(hasInput);
      setMenuOpen(true);
    }
    setInputFocused(true);
  });

  const handleFilterButton = useEventCallback(() => {
    if (menuOpen) {
      if (showSearchFields) {
        setShowSearchFields(false);
      } else {
        setMenuOpen(false);
      }
    } else {
      if (showSearchFields) {
        setShowSearchFields(false);
      }
      setMenuOpen(true);
    }
  });

  // Keyboard focus leaving the box and the panel together closes the panel, as legacy's
  // useTrapFocusState did. Focus moving within them (nested popups included, which React's focus
  // events reach through their portals) keeps it open, and so does a press on a part of the panel
  // that takes no focus (focus then rests on the body); a press outside closes it in onOpenChange.
  const focusInside = useRef(false);
  const onFocusInside = () => {
    focusInside.current = true;
  };
  const onBlurInside = () => {
    focusInside.current = false;
    setTimeout(() => {
      const active = document.activeElement;
      if (!focusInside.current && active && active !== document.body) setMenuOpen(false);
    }, 0);
  };

  const handleSearchOptionSelected = useEventCallback(() => {
    setInput('');
    inputRef.current?.focus();
  });

  const activeChips = useMemo(
    () =>
      activeSearches.map(({ setQuery, name, value, label, key }) => (
        <span key={`${key}-active`} className="m-0.5 inline-flex h-[42px] items-center gap-2 rounded-full bg-muted pr-1 pl-4">
          <span className="flex max-w-36 flex-col items-start leading-tight">
            <span className="text-[10px] font-semibold uppercase">{label}</span>
            <span className="max-w-36 truncate text-sm">{value}</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="rounded-full"
            aria-label={`Remove ${label}`}
            onClick={() => {
              filterButtonRef.current?.focus();
              setQuery({ field: name, value: '', operator: 'ilike' });
            }}
          >
            <XIcon />
          </Button>
        </span>
      )),
    [activeSearches]
  );

  return (
    <div ref={containerRef} className="relative w-full sm:w-[500px] md:w-[650px]" onFocus={onFocusInside} onBlur={onBlurInside}>
      <InputGroup className="h-12 bg-background">
        <InputGroupAddon>
          <SearchIcon aria-hidden className="size-5 text-muted-foreground" />
        </InputGroupAddon>
        <InputGroupInput
          ref={inputRef}
          aria-label="Search"
          placeholder={searchPlaceholder}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onFocus={(event) => {
            event.currentTarget.select();
            handleInputFocus();
          }}
          onBlur={() => setInputFocused(false)}
        />
        <InputGroupAddon align="inline-end">
          <Button
            ref={filterButtonRef}
            type="button"
            variant="outline"
            className="h-9 gap-2 rounded-lg border-muted-foreground"
            aria-label={`Filters (${numSet})`}
            onClick={handleFilterButton}
          >
            <FilterIcon aria-hidden />
            <span className="hidden font-medium md:inline">Filters</span>
            <span className="text-muted-foreground">{` (${numSet})`}</span>
          </Button>
        </InputGroupAddon>
      </InputGroup>
      <PopoverPrimitive.Root
        open={menuOpen}
        modal={false}
        onOpenChange={(open, details) => {
          if (open) return;
          // Pressing the box or its Filters button is not "outside": they drive the panel themselves.
          const target = details.event?.target;
          if (target instanceof Node && containerRef.current?.contains(target)) return;
          setMenuOpen(false);
        }}
      >
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Positioner anchor={containerRef} side="bottom" align="start" sideOffset={4} className="isolate z-[1450]">
            <PopoverPrimitive.Popup
              initialFocus={false}
              finalFocus={false}
              className={cn(
                'flex max-h-[500px] w-(--anchor-width) origin-(--transform-origin) flex-col overflow-y-auto rounded-lg bg-popover p-4 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95'
              )}
            >
              {showSearchFields ? (
                <SearchInputOptions input={input} options={searchFields} onOptionSelected={handleSearchOptionSelected} />
              ) : (
                <div className="flex flex-col gap-4">
                  <ActiveSearchFields>{activeChips}</ActiveSearchFields>
                  {filterControls}
                </div>
              )}
            </PopoverPrimitive.Popup>
          </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  );
}

function ActiveSearchFields({ children }: { children: ReactNode[] }) {
  return (
    <div className="flex w-full flex-col items-stretch">
      <h5 className={cn('text-base font-medium', children.length ? 'text-foreground/75' : 'text-muted-foreground')}>Search Filters</h5>
      <p className="text-xs">Type in the search box to add search filters.</p>
      <p className="text-xs">You can view and remove added search filters below.</p>
      <Separator className="my-2" />
      <div className="flex flex-row flex-wrap items-center">
        {children.length ? children : <span className="text-sm text-muted-foreground">No search filters</span>}
      </div>
    </div>
  );
}

function SearchInputOptions({
  input,
  options,
  onOptionSelected,
}: {
  input: string;
  options: SearchFieldOption[];
  onOptionSelected: (name: string) => void;
}) {
  const choose = useEventCallback((index: number) => {
    const option = options[index];
    if (!input.trim() || isNull(index) || !option) {
      return;
    }
    const { setQuery, name, value } = option;
    if (!value || value.trim() !== input.trim()) {
      setQuery({ field: name, value: input, operator: 'ilike' });
    }
    onOptionSelected(name);
  });
  return (
    <div className="flex flex-col">
      <p className="pb-3 text-base font-medium text-foreground/75">Select a search option to apply</p>
      {options.map((option, i) => (
        <button
          key={option.key}
          type="button"
          aria-label={option.label}
          className="flex h-[38px] w-full items-center justify-start gap-2.5 rounded-md pl-0.5 text-left hover:bg-muted"
          onClick={() => choose(i)}
        >
          <span className="rounded-full bg-muted px-3 py-1 font-medium">{option.label}</span>
          <span className="truncate text-sm text-muted-foreground">{`that includes "${input}"`}</span>
        </button>
      ))}
    </div>
  );
}
